import { useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { ArrowUturnLeftIcon, ClockIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import Modal from "../../layouts/Modal";
import LoadingWheel from "../shared/LoadingWheel";
import Avatar from "../user/Avatar";
import useAuditLogs, { IAuditLog } from "../../data/useAuditLogs";
import useOffers from "../../data/useOffers";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useUserLookup } from "../../hooks/useUserLookup";
import { FormatType, IEbayOffer } from "../../models/EbayOffer";
import { IPart } from "../../models/Part";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";

const PRICE_FIELD_KEYS = ["BuyItNowPrice", "AuctionStartPrice"];

interface IPriceChangeEntry {
  id: string;
  timestamp: string;
  userId: string | null;
  oldPrice: number | null;
  newPrice: number | null;
}

const parsePrice = (value: string | undefined | null): number | null => {
  if (value == null || value === "") return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const extractPriceChanges = (logs: IAuditLog[]): IPriceChangeEntry[] =>
  logs
    .filter((log) => log.action === "Updated" && log.changes)
    .map((log) => {
      let parsed: Record<string, { Old?: string; New?: string }>;
      try {
        parsed = JSON.parse(log.changes!);
      } catch {
        return null;
      }

      const key = PRICE_FIELD_KEYS.find((field) => field in parsed);
      if (!key) return null;

      return {
        id: log.id,
        timestamp: log.timestamp,
        userId: log.userId,
        oldPrice: parsePrice(parsed[key].Old),
        newPrice: parsePrice(parsed[key].New),
      } as IPriceChangeEntry;
    })
    .filter((entry): entry is IPriceChangeEntry => entry != null)
    .sort(
      (a, b) =>
        DateTime.fromISO(b.timestamp).toMillis() - DateTime.fromISO(a.timestamp).toMillis()
    );

function PriceHistoryContent({ part, setOpen }: { part: IPart; setOpen: (open: boolean) => void }) {
  const offer = part.offers?.[0];
  //The audit trail stores the entity type name, and the entity is PartOffer - "EbayOffer" never matches
  const { auditLogs } = useAuditLogs("PartOffer", offer?.id ?? "", "PriceHistoryButton");
  const { update } = useOffers();
  const { saveHandlers } = useDefaultCRUDHandlers("Buy It Now Price");
  const { findUser } = useUserLookup();
  const queryClient = useQueryClient();

  const changes = useMemo(
    () => extractPriceChanges(auditLogs.data?.data ?? []),
    [auditLogs.data?.data]
  );

  const lastChange = changes[0];
  const currentPrice =
    offer?.format === FormatType.Auction ? offer?.auctionStartPrice : offer?.buyItNowPrice;
  const canUndo = !!offer && lastChange?.oldPrice != null && !update.isLoading;

  const handleUndo = () => {
    if (!offer || lastChange?.oldPrice == null) return;

    const updatedOffer: IEbayOffer = { ...offer };
    if (offer.format === FormatType.Auction) {
      updatedOffer.auctionStartPrice = lastChange.oldPrice;
    } else {
      updatedOffer.buyItNowPrice = lastChange.oldPrice;
    }

    update.mutate(updatedOffer, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        queryClient.invalidateQueries(["auditLogs", "PartOffer", offer.id]);
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Buy It Now Price History</h2>
          <p className="text-sm text-gray-500">{offer?.title ?? part.category?.name ?? "Listing"}</p>
        </div>
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-right ring-1 ring-inset ring-amber-200/60">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-900/60">
            Current
          </p>
          <p className="text-base font-semibold tabular-nums text-amber-900">
            {currentPrice != null ? PriceFormat(currentPrice) : "-"}
          </p>
        </div>
      </div>

      {lastChange?.oldPrice != null && (
        <button
          type="button"
          onClick={handleUndo}
          disabled={!canUndo}
          className="flex items-center justify-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 transition hover:bg-primary-100 disabled:opacity-60"
        >
          <ArrowUturnLeftIcon className={classNames("h-4 w-4", update.isLoading && "animate-spin")} />
          Undo last change (back to {PriceFormat(lastChange.oldPrice)})
        </button>
      )}

      <div className="max-h-[24rem] overflow-y-auto">
        {auditLogs.isLoading ? (
          <div className="py-6">
            <LoadingWheel />
          </div>
        ) : changes.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            No price changes recorded yet.
          </p>
        ) : (
          <ol className="space-y-2">
            {changes.map((change, index) => {
              const user = change.userId ? findUser(change.userId) : null;
              const direction =
                change.oldPrice != null && change.newPrice != null
                  ? change.newPrice > change.oldPrice
                    ? "up"
                    : change.newPrice < change.oldPrice
                      ? "down"
                      : "same"
                  : "same";

              return (
                <li
                  key={change.id}
                  className={classNames(
                    "rounded-lg border px-3 py-2",
                    index === 0 ? "border-primary-200 bg-primary-50/40" : "border-gray-100 bg-white"
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm tabular-nums">
                      <span className="text-gray-400 line-through">
                        {change.oldPrice != null ? PriceFormat(change.oldPrice) : "-"}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span
                        className={classNames(
                          "font-semibold",
                          direction === "up" && "text-emerald-600",
                          direction === "down" && "text-red-600",
                          direction === "same" && "text-gray-700"
                        )}
                      >
                        {change.newPrice != null ? PriceFormat(change.newPrice) : "-"}
                      </span>
                      {index === 0 && (
                        <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                          Latest
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatDateTime(change.timestamp, DateTime.DATETIME_MED)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-1.5">
                    {change.userId ? (
                      <>
                        <Avatar userId={change.userId} classes="w-5 h-5" textClasses="!text-[10px]" />
                        <span className="text-xs text-gray-500">
                          {user?.fullname ?? "Unknown"}
                        </span>
                      </>
                    ) : (
                      <span className="text-xs italic text-gray-400">System</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default function PriceHistoryButton({ part }: { part: IPart }) {
  const [open, setOpen] = useState(false);

  if (!part.offers?.[0]) return null;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="View price history"
        className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 transition hover:text-primary-700"
      >
        <ClockIcon className="h-3.5 w-3.5" aria-hidden />
        Price history
      </button>

      <Modal open={open} setOpen={setOpen} width="max-w-lg">
        {open && <PriceHistoryContent part={part} setOpen={setOpen} />}
      </Modal>
    </div>
  );
}
